import * as React from 'react';
// Import hooks.
import {
	useSubformPropertyAddress,
	UseSubformPropertyAddressResults,
} from '@app/hooks/view';
import {
	UseInputAddressState,
	UseInputFormResult,
} from '@app/hooks/view/input';
// Import props.
import {
	FormPropertyAddressBaseProps,
} from '@app/props';
// Import api models.
import {
	AddressLocationAsMailingAddress,
	GpsCoordinates,
} from '@app/models/api';

interface UseFormPropertyAddressBaseParameters {
	id: string;
	isFormDisabled: boolean;
	props: FormPropertyAddressBaseProps,
	requestToChangeLocation?: () => void;
}

interface UseFormPropertyAddressBaseSharedResults {
	areAllInputsDisabled: boolean;
	subformPropertyAddress: UseSubformPropertyAddressResults;
}

interface UseFormPropertyAddressBaseResults extends UseFormPropertyAddressBaseSharedResults {
	submitForm: (checkPostValidationConditions?: () => boolean) => void;
	checkFormValidation: (focusToElement: boolean) => boolean;
}

const useFormPropertyAddressBase = (params: UseFormPropertyAddressBaseParameters): UseFormPropertyAddressBaseResults => {

	// #region exported methods

	/**
	 *  Starts the submission process with validation for the address.
	 **/
	const submitForm = (checkPostValidationConditions?: () => boolean): void => {
		// First flag all inputs to show any validation error.s
		flagAllInputsToShowValidations();
		// Validate the whole form.
		let isValid: boolean = checkFormValidation(true);
		// Make sure the confirmation popup appears if it's required.
		if (isValid) {
			if (checkPostValidationConditions === undefined
				|| checkPostValidationConditions()) {
				subformPropertyAddress.verifyAddress();
			}
		}
	}

	// #endregion exported methods


	// #region private methods

	/**
	 * Finishes and saves the address.
	 * @param address
	 */
	const onFinishVerifyAddress = (address: AddressLocationAsMailingAddress, coordinates: GpsCoordinates): void => {
		params.props.onSavePropertyAddress(address);
	}

	/**
	 * Validates an individual input.
	 * @param input
	 */
	const checkInputValidation = <T>(input: UseInputFormResult<T>, focusToElement: boolean): boolean => {
		let isValid = input.isInputValid;
		if (!isValid && focusToElement) {
			input.focusToInput();
		}
		return isValid;
	}

	/**
	 * Method to check form validation. 
	 **/
	const checkFormValidation = (focusToElement: boolean): boolean => {
		return checkInputValidation<UseInputAddressState>(subformPropertyAddress.addressGeocoding.addressVerification.addressLocationInput, focusToElement)
			&& checkInputValidation<boolean | undefined>(subformPropertyAddress.copyToMailingAddressInput, focusToElement);
	}

	/**
	 * Flags all inputs to show the validation messages. 
	 **/
	const flagAllInputsToShowValidations = (): void => {
		subformPropertyAddress.flagAllInputsToShowValidations();
	}

	/**
	 * Generates id. 
	 **/
	const generateId = (): string => {
		return params.id;
	}

	/**
	 * Generates id for the subform property address. 
	 **/
	const generateIdForSubformPropertyAddress = (): string => {
		return `${generateId()}-subform-propertyAddress`;
	}

	// #endregion private methods

	// #region variables

	const showValidation: boolean = React.useMemo(() => params.props.showValidationsOnMount ?? false, [params.props.showValidationsOnMount]);

	// #region Input hooks.

	const subformPropertyAddress: UseSubformPropertyAddressResults = useSubformPropertyAddress({
		id: generateIdForSubformPropertyAddress(),
		allowEdits: !params.isFormDisabled,
		broadLineOfBusinessCode: params.props.broadLineOfBusinessCode,
		inputSettingsProps: params.props.inputSettingsProps?.subformInputSettingsPropertyAddressProps,
		showValidationsOnMount: showValidation,
		possibleStates: params.props.possibleStates,
		propertyAddress: params.props.propertyAddress,
		onRequestToChangeLocation: params.props.onRequestToChangeLocation,
		onFinishVerifyAddress,
		addressProps: params.props.addressProps,
	});
	

	// #endregion Input hooks.

	// Check to see if all inputs are disabled.
	const areAllInputsDisabled: boolean = React.useMemo(() => {
		return subformPropertyAddress.areAllInputsDisabled;
	}, [
		subformPropertyAddress.areAllInputsDisabled,
	]);

	// #endregion variables


	// #region life-cycle methods.

	// Called when the hook is initially mounted, and returns a call back that will be called on unmounting.
	React.useEffect(() => {

		// If validations are to be shown, then initially scroll to the first invalidated input.
		if (!showValidation
			|| checkFormValidation(true)) {
			// If validations pass or are not to be shown, then initially the first input should be focused.
			subformPropertyAddress.addressGeocoding.addressVerification.addressLocationInput.focusToInput();
		}
	}, []);

	// #endregion life-cycle methods.


	return {
		areAllInputsDisabled,
		subformPropertyAddress,
		submitForm,
		checkFormValidation,
	};
}

// Export parameters.
export {
	UseFormPropertyAddressBaseParameters,
}

// Export results.
export {
	UseFormPropertyAddressBaseResults,
	UseFormPropertyAddressBaseSharedResults,
}

export default useFormPropertyAddressBase;